import { api, TAGS } from "@/store/api"
import {
  IAllocateAttributePointsRequest,
  IAllocateAttributePointsResponse,
  IAllocateSkillPointsRequest,
  IAllocateSkillPointsResponse,
  IDevGrantXpRequest,
  IDevResetRequest,
  IDevSetEffectRemainingRequest,
  IDevSetEffectRemainingResponse,
  IDevVitalAmountRequest,
  IDevVitalResponse,
  IDevVitalSetRequest,
  IGrantXpResponse,
  IHeroMeResponse,
  IHeroProgressionState
} from "@/store/apis/heroAPI/hero.types"
import { reportUnparsableServerTime } from "@/store/apis/heroAPI/heroServerTime"

export const heroAPI = api
  .enhanceEndpoints({ addTagTypes: [TAGS.HERO, TAGS.INVENTORY] })
  .injectEndpoints({
    endpoints: (build) => ({
      getHeroMe: build.query<IHeroMeResponse, void>({
        query: () => ({ url: "/hero/me", method: "GET" }),
        transformResponse: reportUnparsableServerTime,
        providesTags: [TAGS.HERO]
      }),
      allocateAttributePoints: build.mutation<
        IAllocateAttributePointsResponse,
        IAllocateAttributePointsRequest
      >({
        query: (data) => ({
          url: "/hero/attributes/allocate",
          method: "POST",
          data
        }),
        invalidatesTags: [TAGS.HERO, TAGS.INVENTORY]
      }),
      allocateSkillPoints: build.mutation<
        IAllocateSkillPointsResponse,
        IAllocateSkillPointsRequest
      >({
        query: (data) => ({
          url: "/hero/skills/allocate",
          method: "POST",
          data
        }),
        // HERO only, deliberately not INVENTORY: attribute values gate item
        // requirements, skill ranks do not (main-backend's ADR-0031 keeps
        // 'SKILL' out of ALLOCATION_ONLY_SOURCES), so refetching the
        // inventory here is waste.
        invalidatesTags: [TAGS.HERO]
      }),
      devGrantXp: build.mutation<IGrantXpResponse, IDevGrantXpRequest>({
        query: (data) => ({ url: "/hero/dev/grant-xp", method: "POST", data }),
        invalidatesTags: [TAGS.HERO, TAGS.INVENTORY]
      }),
      devResetHero: build.mutation<IHeroProgressionState, IDevResetRequest>({
        query: (data) => ({ url: "/hero/dev/reset", method: "POST", data }),
        invalidatesTags: [TAGS.HERO, TAGS.INVENTORY]
      }),
      devVitalAdd: build.mutation<IDevVitalResponse, IDevVitalAmountRequest>({
        query: (data) => ({
          url: "/hero/dev/vitals/add",
          method: "POST",
          data
        }),
        invalidatesTags: [TAGS.HERO]
      }),
      devVitalRemove: build.mutation<IDevVitalResponse, IDevVitalAmountRequest>(
        {
          query: (data) => ({
            url: "/hero/dev/vitals/remove",
            method: "POST",
            data
          }),
          invalidatesTags: [TAGS.HERO]
        }
      ),
      devVitalSet: build.mutation<IDevVitalResponse, IDevVitalSetRequest>({
        query: (data) => ({
          url: "/hero/dev/vitals/set",
          method: "POST",
          data
        }),
        invalidatesTags: [TAGS.HERO]
      }),
      devSetEffectRemaining: build.mutation<
        IDevSetEffectRemainingResponse,
        IDevSetEffectRemainingRequest
      >({
        query: (data) => ({
          url: "/hero/dev/effects/set-remaining",
          method: "POST",
          data
        }),
        invalidatesTags: [TAGS.HERO]
      })
    })
  })

export const {
  useGetHeroMeQuery,
  useAllocateAttributePointsMutation,
  useAllocateSkillPointsMutation,
  useDevGrantXpMutation,
  useDevResetHeroMutation,
  useDevVitalAddMutation,
  useDevVitalRemoveMutation,
  useDevVitalSetMutation,
  useDevSetEffectRemainingMutation
} = heroAPI
